// Plain-language view of SSS, PhilHealth and Pag-IBIG contributions on a payslip (CD-S2). The server decides every
// amount and every state; this file only names them for people. It never shows a government number.

export const AGENCY_NAME = { SSS: "SSS", PHILHEALTH: "PhilHealth", PAGIBIG: "Pag-IBIG", BIR_WHT: "Withholding tax" };

const STATE = {
  AUTO_CALCULATED: { label: "Calculated", tone: "ok" },
  MANUALLY_REVIEWED: { label: "Reviewed by HR", tone: "reviewed" },
  REVIEW_REQUIRED: { label: "Needs review", tone: "review" },
  BLOCKED: { label: "Cannot be worked out", tone: "blocked" },
};

export const peso = (cents) => new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(Number(cents || 0) / 100);

/** A line whose amounts are final for this payslip (worked out, or entered by HR review). */
export const isDecided = (line) => ["AUTO_CALCULATED", "MANUALLY_REVIEWED"].includes(line?.state);

// In a list the document name is enough; the full title is shown where a reader asks for it.
export const shortRuleName = (title) => String(title || "").split(" - ")[0];

export function contributionState(line) {
  return STATE[line?.state] || { label: "Not decided", tone: "review" };
}

/** One short line under an agency: what its amount was worked out from. */
export function basisText(line) {
  const detail = line?.detail || {};
  if (line?.basisCents == null) return null;
  if (line.agency === "SSS") {
    return `Pay for the month ${peso(line.basisCents)} → salary credit ${peso(detail.mscCents)}`;
  }
  if (line.agency === "PHILHEALTH") {
    const limit = detail.atFloor ? " (the minimum basis applies)" : detail.atCeiling ? " (the maximum basis applies)" : "";
    return `Monthly basic salary ${peso(line.basisCents)}${limit}`;
  }
  if (line.agency === "PAGIBIG") {
    return `Fund salary ${peso(line.basisCents)}${detail.capped ? " (the maximum applies)" : ""}`;
  }
  return null;
}

// Shown only when Apparify's own rounding fallback actually changed the Pag-IBIG amount. Never called a Pag-IBIG rule.
export const APPARIFY_ROUNDING_NOTE = "Rounded to the nearest centavo by Apparify's own rule. Pag-IBIG publishes no rounding rule.";

export function wasRoundedByApparify(line) {
  return Boolean(line?.appliedPolicyKey);
}

/** The employer's side of a payslip's contributions, one row per item; EC is an SSS item paid by the employer only. */
export function employerRows(lines = []) {
  const rows = [];
  for (const line of lines) {
    if (!["AUTO_CALCULATED", "MANUALLY_REVIEWED"].includes(line.state)) continue;
    rows.push({ key: line.agency, label: `${AGENCY_NAME[line.agency]} employer share`, cents: Number(line.employerCents || 0) });
    if (line.agency === "SSS" && Number(line.ecCents || 0) > 0) {
      rows.push({ key: "SSS_EC", label: "SSS Employees' Compensation (EC)", cents: Number(line.ecCents) });
    }
  }
  return rows;
}

/** "150.5" or "1,500.00" typed by a person → whole centavos, or null when it is not a valid amount. */
export function parsePesoInput(value) {
  const text = String(value ?? "").replace(/[,\s₱]/g, "");
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
}

/** `employeeOnly`: withholding tax has no employer amount (CD-S3). */
export function reviewFormErrors({ employee, employer, reason, grossCents, employeeOnly = false }) {
  const errors = {};
  const employeeCents = parsePesoInput(employee);
  const employerCents = employeeOnly ? 0 : parsePesoInput(employer);
  const what = employeeOnly ? "withholding tax" : "employee amount";
  if (employeeCents == null) errors.employee = `Enter the ${what} in pesos, for example 250.00.`;
  else if (grossCents != null && employeeCents > grossCents) errors.employee = `The ${what} cannot be more than the pay on this payslip.`;
  if (employerCents == null) errors.employer = "Enter the employer amount in pesos, for example 250.00.";
  const text = String(reason || "").trim();
  if (text.length < 5) errors.reason = "Say why this amount is right (at least 5 characters).";
  else if (text.length > 500) errors.reason = "Keep the reason within 500 characters.";
  return { errors, employeeCents, employerCents, reason: text };
}
